export type C1_LightImageHitRect = {
  imageWidth: number;
  imageHeight: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

const hitRectCache = new Map<string, C1_LightImageHitRect>();
const pendingHitRects = new Map<string, Promise<C1_LightImageHitRect | undefined>>();

export function cachedLightImageHitRect(url: string): C1_LightImageHitRect | undefined {
  return hitRectCache.get(url);
}

export function loadLightImageHitRect(url: string): Promise<C1_LightImageHitRect | undefined> {
  const cached = hitRectCache.get(url);
  if (cached) return Promise.resolve(cached);
  const pending = pendingHitRects.get(url);
  if (pending) return pending;

  const promise = new Promise<C1_LightImageHitRect | undefined>((resolve) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context || !canvas.width || !canvas.height) {
        resolve(undefined);
        return;
      }
      context.drawImage(image, 0, 0);
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
      let minX = canvas.width;
      let minY = canvas.height;
      let maxX = -1;
      let maxY = -1;
      for (let y = 0; y < canvas.height; y += 1) {
        for (let x = 0; x < canvas.width; x += 1) {
          if (pixels[(y * canvas.width + x) * 4 + 3] === 0) continue;
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
      const result = maxX < minX || maxY < minY
        ? undefined
        : {
            imageWidth: canvas.width,
            imageHeight: canvas.height,
            x: minX,
            y: minY,
            width: maxX - minX + 1,
            height: maxY - minY + 1,
          };
      if (result) hitRectCache.set(url, result);
      resolve(result);
    };
    image.onerror = () => resolve(undefined);
    image.src = url;
  }).finally(() => pendingHitRects.delete(url));

  pendingHitRects.set(url, promise);
  return promise;
}

